/**
 * OCTOPUS - EngineModelsManager
 * Gerenciador do CATÁLOGO de modelos de motor (marca/modelo/rating + load curve).
 *
 * Decisão do dono 2026-07-08: a curva vive SÓ no catálogo (o motor herda do modelo,
 * sem override na tela do equipamento). Aqui é onde a curva é criada/editada — com o
 * LoadCurveEditor (tabela / CSV / cálculo assistido / preview). Acessível pelo caminho
 * Equipamentos → cadastro → Telemetria → "Modelos de Motor" (organizado, não campo solto).
 *
 * D-20 (Plan 14-06): "Modelos de Motor" também é um CATÁLOGO de frota, com página própria
 * em CADASTROS › Equipamentos (`/engine-models`). `EngineModelsPanel` é o corpo (lista +
 * editor + LoadCurveEditor) extraído para ser embutido inline numa página OU num modal.
 * `EngineModelsManager` (default export) continua a fachada de modal usada pelo MotorsConfig
 * — mesma API pública `{ onChanged, triggerClassName }`, agora renderizando o painel por dentro.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Cog, Plus, Trash2, Pencil, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { showErrorToast } from '@/utils/errorHandler';
import LoadCurveEditor from './LoadCurveEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { brand: '', model: '', rating: '', power_kw: '', load_curve: [] };

const modelLabel = (m) => [m.brand, m.model, m.rating && `(${m.rating})`].filter(Boolean).join(' ');

// EngineModelsPanel: corpo inline reusável (lista + editor + LoadCurveEditor).
// Sem chrome de Dialog — auto-busca no mount (não depende de nenhum `open` de modal).
// Usado tanto por EngineModelsPage (inline) quanto por EngineModelsManager (dentro do modal).
export const EngineModelsPanel = ({ onChanged }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = lista; {} novo; {id...} edição
  const [saving, setSaving] = useState(false);

  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/engine-models`);
      setModels(res.data || []);
    } catch (error) {
      showErrorToast(toast, error, 'Erro ao carregar modelos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchModels(); }, [fetchModels]);

  const save = async () => {
    if (!editing.brand?.trim() || !editing.model?.trim()) {
      toast.error('Informe marca e modelo');
      return;
    }
    if (!editing.load_curve || editing.load_curve.length < 2) {
      toast.error('A curva precisa de ao menos 2 pontos');
      return;
    }
    const body = {
      brand: editing.brand.trim(), model: editing.model.trim(),
      rating: editing.rating || null,
      power_kw: editing.power_kw === '' ? null : Number(editing.power_kw),
      load_curve: editing.load_curve.map((p) => ({ rpm: Number(p.rpm), max_lph: Number(p.max_lph) })),
    };
    try {
      setSaving(true);
      if (editing.id) {
        await axios.put(`${API}/engine-models/${editing.id}`, body);
        toast.success('Modelo atualizado (curva propagada aos motores associados)');
      } else {
        await axios.post(`${API}/engine-models`, body);
        toast.success('Modelo cadastrado');
      }
      await fetchModels();
      setEditing(null);
      onChanged?.();
    } catch (error) {
      showErrorToast(toast, error, 'Erro ao salvar modelo');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (model) => {
    try {
      await axios.delete(`${API}/engine-models/${model.id}`);
      toast.success('Modelo removido');
      await fetchModels();
      onChanged?.();
    } catch (error) {
      // 409 = em uso por motores
      showErrorToast(toast, error, 'Não foi possível remover o modelo');
    }
  };

  return (
    <div className="space-y-3">
      {/* LISTA */}
      {editing === null && (
          <div className="space-y-2">
            <div className="flex justify-end">
              <Button type="button" size="sm" className="gap-1" onClick={() => setEditing({ ...EMPTY })}
                data-testid="engine-model-new">
                <Plus className="h-3.5 w-3.5" /> Novo modelo
              </Button>
            </div>
            {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground py-3"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</div>}
            {!loading && models.length === 0 && (
              <p className="text-sm text-muted-foreground py-3">Nenhum modelo cadastrado ainda.</p>
            )}
            {!loading && models.map((m) => (
              <div key={m.id} className="flex items-center gap-2 rounded-md border border-border p-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{modelLabel(m)}</p>
                  <p className="text-xs text-muted-foreground">
                    {(m.load_curve?.length || 0)} pontos{m.power_kw ? ` · ${m.power_kw} kW` : ''}
                  </p>
                </div>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" title="Editar"
                  onClick={() => setEditing({ ...m, power_kw: m.power_kw ?? '', rating: m.rating ?? '' })}
                  data-testid={`engine-model-edit-${m.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      title="Remover" data-testid={`engine-model-del-${m.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remover {modelLabel(m)}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        O modelo é compartilhado entre equipamentos. Se estiver em uso por algum motor,
                        a remoção é bloqueada — desassocie os motores antes. Motores sem modelo deixam
                        de calcular a carga.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(m)}>Remover</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}

        {/* EDITOR */}
        {editing !== null && (
          <div className="space-y-3">
            <Button type="button" variant="ghost" size="sm" className="gap-1 -ml-2 h-7 text-xs"
              onClick={() => setEditing(null)}><ArrowLeft className="h-3.5 w-3.5" /> Voltar à lista</Button>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Marca</Label>
                <Input className="h-8 text-sm" placeholder="Ex.: Volvo Penta" value={editing.brand}
                  onChange={(e) => setEditing({ ...editing, brand: e.target.value })} data-testid="engine-model-brand" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Modelo</Label>
                <Input className="h-8 text-sm" placeholder="Ex.: D13-MH" value={editing.model}
                  onChange={(e) => setEditing({ ...editing, model: e.target.value })} data-testid="engine-model-model" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Rating</Label>
                <Input className="h-8 text-sm" placeholder="Ex.: 600" value={editing.rating}
                  onChange={(e) => setEditing({ ...editing, rating: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Potência nominal (kW) — opcional</Label>
                <Input className="h-8 text-sm" type="number" placeholder="Ex.: 441" value={editing.power_kw}
                  onChange={(e) => setEditing({ ...editing, power_kw: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Curva de plena carga (RPM × L/h)</Label>
              <LoadCurveEditor value={editing.load_curve} onChange={(c) => setEditing({ ...editing, load_curve: c })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="button" size="sm" onClick={save} disabled={saving} data-testid="engine-model-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                {editing.id ? 'Salvar alterações' : 'Cadastrar modelo'}
              </Button>
            </div>
          </div>
        )}
    </div>
  );
};

// EngineModelsManager: fachada de MODAL usada pelo MotorsConfig (aba Telemetria).
// API pública inalterada `{ onChanged, triggerClassName }` — renderiza EngineModelsPanel
// como corpo do Dialog.
const EngineModelsManager = ({ onChanged, triggerClassName }) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className={`gap-1.5 ${triggerClassName || ''}`}
          data-testid="engine-models-open">
          <Cog className="h-3.5 w-3.5" /> Modelos de Motor
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cog className="h-4 w-4" /> Catálogo de Modelos de Motor
          </DialogTitle>
          <DialogDescription>
            Marca, modelo, rating e a curva de plena carga (para o cálculo de carga do motor).
            Cada motor do equipamento herda a curva do modelo associado.
          </DialogDescription>
        </DialogHeader>
        <EngineModelsPanel onChanged={onChanged} />
      </DialogContent>
    </Dialog>
  );
};

export default EngineModelsManager;
